import { supabase } from '../lib/supabase';
import { VeiculoChecklist, VeiculoAbastecimento } from '../types/veiculoExtra';

const KEY_STATUS_MAP = 'veiculos_status_frota_map';

export const veiculoExtraService = {
  // Busca o mapa de status salvos para mesclar com os veículos
  async getStatusFrotaMap(): Promise<Record<string, { status_frota: string; motivo_manutencao?: string | null; previsao_entrega?: string | null }>> {
    let map: Record<string, any> = {};
    // 1. Tenta carregar do localStorage
    try {
      const local = localStorage.getItem(KEY_STATUS_MAP);
      if (local) map = JSON.parse(local);
    } catch (_) {}

    // 2. Tenta carregar do system_preference no Supabase
    // A tabela tem apenas as colunas `id` e `preference` — usar preference_key/
    // preference_value fazia a leitura falhar sempre e o status ficava só local.
    try {
      const { data, error } = await supabase
        .from('system_preference')
        .select('preference')
        .eq('id', KEY_STATUS_MAP)
        .maybeSingle();

      if (error) {
        console.warn('[veiculoExtraService] Erro ao ler status da frota:', error.message);
      } else if (data?.preference) {
        const parsed = JSON.parse(data.preference);
        map = { ...map, ...parsed };
      }
    } catch (e) {
      console.warn('[veiculoExtraService] Falha ao interpretar status da frota:', e);
    }

    return map;
  },

  // --- MANUTENÇÃO & STATUS DA FROTA ---
  async updateStatusManutencao(
    veiculoId: string,
    status: 'disponivel' | 'em_manutencao' | 'em_uso' | 'desativado',
    motivo?: string | null,
    previsao?: string | null
  ) {
    // A tabela `veiculos` não possui as colunas ativo / data_desativacao /
    // status_frota / motivo_manutencao / previsao_entrega. Escrever nelas só
    // gerava requisições rejeitadas. O mapa em system_preference é a fonte real.
    const registro = {
      status_frota: status,
      motivo_manutencao: status === 'em_manutencao' ? motivo ?? null : null,
      previsao_entrega: status === 'em_manutencao' ? previsao ?? null : null,
    };

    const currentMap = await this.getStatusFrotaMap();
    currentMap[String(veiculoId)] = registro;
    const jsonStr = JSON.stringify(currentMap);

    try {
      localStorage.setItem(KEY_STATUS_MAP, jsonStr);
    } catch (e) {
      console.warn('Aviso ao salvar status da frota no cache local:', e);
    }

    const { error } = await supabase
      .from('system_preference')
      .upsert({ id: KEY_STATUS_MAP, preference: jsonStr });

    if (error) {
      throw new Error(`Não foi possível salvar o status da frota: ${error.message}`);
    }

    return {
      id: veiculoId,
      ativo: status !== 'desativado',
      data_desativacao: status === 'desativado' ? new Date().toISOString() : null,
      ...registro,
    };
  },

  // --- CHECKLIST PRÉ-VIAGEM ---
  async getChecklists(veiculoId?: string): Promise<VeiculoChecklist[]> {
    try {
      let query = supabase.from('veiculo_checklists').select('*').order('data_checklist', { ascending: false });
      if (veiculoId) {
        query = query.eq('veiculo_id', veiculoId);
      }
      const { data, error } = await query;
      if (error) {
        console.warn('Notice loading veiculo_checklists:', error.message);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error('Error fetching checklists:', err);
      return [];
    }
  },

  async createChecklist(checklist: VeiculoChecklist): Promise<VeiculoChecklist> {
    const { data, error } = await supabase
      .from('veiculo_checklists')
      .insert([checklist])
      .select()
      .single();

    // A tabela existe: se falhar, é erro de verdade e precisa chegar na tela.
    if (error) throw new Error(`Não foi possível salvar o checklist: ${error.message}`);
    return data;
  },

  // --- ABASTECIMENTO E CÁLCULO DE CONSUMO ---
  async getAbastecimentos(veiculoId?: string): Promise<VeiculoAbastecimento[]> {
    try {
      let query = supabase.from('veiculo_abastecimentos').select('*').order('km_atual', { ascending: false });
      if (veiculoId) {
        query = query.eq('veiculo_id', veiculoId);
      }
      const { data, error } = await query;
      if (error) {
        console.warn('Notice loading veiculo_abastecimentos:', error.message);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error('Error fetching abastecimentos:', err);
      return [];
    }
  },

  async createAbastecimento(abastecimento: VeiculoAbastecimento): Promise<VeiculoAbastecimento> {
    // Calculo de KM percorrido e médias
    const kmPercorrido =
      abastecimento.km_anterior !== null && abastecimento.km_anterior !== undefined
        ? Math.max(0, abastecimento.km_atual - abastecimento.km_anterior)
        : 0;

    const consumoKmL =
      abastecimento.litros > 0 && kmPercorrido > 0
        ? Number((kmPercorrido / abastecimento.litros).toFixed(2))
        : null;

    const precoLitro =
      abastecimento.litros > 0
        ? Number((abastecimento.valor_total / abastecimento.litros).toFixed(2))
        : null;

    const custoKm =
      kmPercorrido > 0
        ? Number((abastecimento.valor_total / kmPercorrido).toFixed(2))
        : null;

    const fullPayload: VeiculoAbastecimento = {
      ...abastecimento,
      km_percorrido: kmPercorrido,
      consumo_km_l: consumoKmL,
      preco_litro: precoLitro,
      custo_km: custoKm,
    };

    const { data, error } = await supabase
      .from('veiculo_abastecimentos')
      .insert([fullPayload])
      .select()
      .single();

    // Obs.: a tabela `veiculos` não tem coluna km_atual — o KM corrente é
    // derivado do abastecimento mais recente (getAbastecimentos ordena por km).

    if (error) throw new Error(`Não foi possível salvar o abastecimento: ${error.message}`);

    return data;
  },
};
